'use client';
import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

const FALLBACK_LOGO = 'https://celsoftcorporation.com/wp-content/uploads/2023/05/rsz_oie_jubobkqwsiwi_1.png';
const LOGO_UPDATED_EVENT = 'site-logo-updated';
const DEFAULT_LOGO_MAX_WIDTH = 350;

export function useSiteLogo(): { logoUrl: string; darkLogoUrl: string; isCustomLogo: boolean; loading: boolean; logoMaxWidth: number } {
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [darkLogoUrl, setDarkLogoUrl] = useState<string>('');
  const [isCustomLogo, setIsCustomLogo] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [logoMaxWidth, setLogoWidth] = useState<number>(DEFAULT_LOGO_MAX_WIDTH);

  const fetchLogo = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', ['site_logo_url', 'logo_max_width', 'site_logo_dark_url']);

      const logoRow = data?.find((r: { key: string; value: string }) => r.key === 'site_logo_url');
      const maxWidthRow = data?.find((r: { key: string; value: string }) => r.key === 'logo_max_width');
      const darkLogoRow = data?.find((r: { key: string; value: string }) => r.key === 'site_logo_dark_url');

      const url = logoRow?.value || FALLBACK_LOGO;
      const isCustom = !!(logoRow?.value && logoRow.value.trim() !== '' && logoRow.value !== FALLBACK_LOGO);
      setLogoUrl(url);
      setIsCustomLogo(isCustom);

      const parsedWidth = maxWidthRow?.value ? parseInt(maxWidthRow.value, 10) : DEFAULT_LOGO_MAX_WIDTH;
      setLogoWidth(isNaN(parsedWidth) ? DEFAULT_LOGO_MAX_WIDTH : parsedWidth);

      // Dark logo: use it if set, otherwise fall back to empty string (callers will use logoUrl as fallback)
      setDarkLogoUrl(darkLogoRow?.value && darkLogoRow.value.trim() !== '' ? darkLogoRow.value : '');
    } catch {
      setLogoUrl(FALLBACK_LOGO);
      setDarkLogoUrl('');
      setIsCustomLogo(false);
      setLogoWidth(DEFAULT_LOGO_MAX_WIDTH);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogo();

    const handler = () => {
      setLoading(true);
      fetchLogo();
    };

    window.addEventListener(LOGO_UPDATED_EVENT, handler);
    return () => window.removeEventListener(LOGO_UPDATED_EVENT, handler);
  }, [fetchLogo]);

  return { logoUrl, darkLogoUrl, isCustomLogo, loading, logoMaxWidth: logoMaxWidth };
}

export function invalidateSiteLogoCache() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOGO_UPDATED_EVENT));
  }
}

export { FALLBACK_LOGO };
